import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import type { Followup } from '../types'
import { groupFollowups } from '../utils'
import LoopCard from './LoopCard'

interface Props {
  items: Followup[]
  expandedId: string | null
  onToggle: (id: string) => void
  onResolve: (id: string) => void
  onSnooze: (id: string, until: string) => void
  onUpdateDueAt: (id: string, dueAt: string | null) => void
}

export default function GroupedView({ items, expandedId, onToggle, onResolve, onSnooze, onUpdateDueAt }: Props) {
  const { t } = useTranslation('followups')
  const groups = groupFollowups(items)

  return (
    <div className="space-y-6">
      {groups.map((group) => {
        const openCount = group.items.filter(i => i.status === 'open').length
        const soonestDue = group.items
          .filter(i => i.dueAt && i.status === 'open')
          .sort((a, b) => new Date(a.dueAt!).getTime() - new Date(b.dueAt!).getTime())[0]

        return (
          <motion.div
            key={group.name ?? '__ungrouped'}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Group header */}
            <div className="flex items-center gap-2 mb-2 px-1">
              <h3 className="text-sm font-medium text-ivory/80">
                {group.name ?? t('ungrouped')}
              </h3>
              {openCount > 0 && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gold/15 text-gold">
                  {t('groupSummary.openCount', { count: openCount })}
                </span>
              )}
              {soonestDue?.dueAt && (
                <span className="text-[10px] text-pearl/60 ml-auto">
                  {new Date(soonestDue.dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>

            {/* Group items */}
            <div className="space-y-2">
              {group.items.map(loop => (
                <LoopCard
                  key={loop.id}
                  loop={loop}
                  isExpanded={expandedId === loop.id}
                  onToggle={() => onToggle(loop.id)}
                  onResolve={onResolve}
                  onSnooze={onSnooze}
                  onUpdateDueAt={onUpdateDueAt}
                />
              ))}
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}
